"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { AiOutlineClose, AiOutlineMenu } from "react-icons/ai";
import { BiChevronDown, BiLinkExternal } from "react-icons/bi";
import { BsChevronDown } from "react-icons/bs";
import { RiAlertFill } from "react-icons/ri";
import AlertaModal from "../AlertaModal/AlertaModal";
import CookiePreferencesButton from "@/app/components/CookieConsent/CookiePreferencesButton";
import SacContactModal from "@/app/components/SacContactModal/SacContactModal";
import { LinksNavigation, NavigationLines } from "./NavigationInfo";
import CustomLink from "@/app/components/CustomLink";
import { BRAND_NAMES } from "@/app/utils/utils";

export const Header = () => {
  const path = usePathname();
  const [activeDropdown, setActiveDropdown] = useState<
    LinksNavigation | undefined
  >();
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [sacModalOpen, setSacModalOpen] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openedDropdown, setOpenedDropdown] = useState({
    menu: "",
    submenu: "",
  });
  const [show, setShow] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const controlNavbar = () => {
    if (window.scrollY > lastScrollY) {
      setShow(false);
    } else {
      setShow(true);
    }
    setLastScrollY(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener("scroll", controlNavbar);

    return () => {
      window.removeEventListener("scroll", controlNavbar);
    };
  }, [lastScrollY]);

  useEffect(() => {
    setMobileMenu(false);
    setOpenedDropdown({ menu: "", submenu: "" });
    setActiveDropdown(undefined);
    document.body.classList.remove("overflow-hidden"); // Remover overflow ao mudar de rota
  }, [path]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const headerNav = document.querySelector("nav");
      const dropdown = document.querySelector('[key="divACtiveContent"]');
      const dropdownContainer = document.querySelector(".dropdown-container");
      const target = event.target as HTMLElement;

      // Verifica se o clique não foi em um botão do menu
      const isMenuButton = target.closest("button");
      const isInsideNav = headerNav?.contains(target);
      const isInsideDropdown =
        dropdown?.contains(target) || dropdownContainer?.contains(target);

      if (
        activeDropdown &&
        !isMenuButton &&
        !isInsideNav &&
        !isInsideDropdown
      ) {
        setActiveDropdown(undefined);
      }
    };

    if (activeDropdown) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeDropdown]);

  const toggleMobileMenu = () => {
    setMobileMenu(!mobileMenu);
    if (!mobileMenu) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  };

  const openSacModalFromMenu = () => {
    setSacModalOpen(true);
    setActiveDropdown(undefined);
    setMobileMenu(false);
    setOpenedDropdown({ menu: "", submenu: "" });
    document.body.classList.remove("overflow-hidden");
  };

  return (
    <>
      <header
        className={`flex w-full lg:h-16 items-center z-40 justify-between bg-white px-4 py-2 transition-all duration-300 shadow-md shadow-black/40 ${
          show ? "max-lg:top-0" : "max-lg:-top-8"
        }`}
      >
        <div className="w-full max-lg:flex-wrap max-w-[1140px] mx-auto flex items-center justify-between">
          <div className="lg:hidden flex w-full items-end border-b pb-1 mb-1">
            <button
              onClick={() => {
                setAlertIsOpen(true);
              }}
              className="w-100 mr-10 flex w-fit ml-auto items-center text-left text-sm text-gray-700 hover:text-black"
            >
              Alerta aos consumidores
            </button>
            <RiAlertFill size={20} className=" text-red-500" />
          </div>
          <div className="lg:ml-10 flex items-center space-x-4">
            <CustomLink
              href={"/"}
              className="max-md:w-full"
              onClick={() => {
                setActiveDropdown(undefined);
                setOpenedDropdown({ menu: "", submenu: "" });
              }}
            >
              <img
                src={BRAND_NAMES.siteLogoMenu}
                alt="Toyota Logo"
                width={164}
                height={52}
                className="w-[164px]"
              />
            </CustomLink>
          </div>
          {!mobileMenu ? (
            <AiOutlineMenu
              size={28}
              className="text-black lg:hidden"
              onClick={toggleMobileMenu}
            />
          ) : (
            <AiOutlineClose
              size={28}
              className="text-black lg:hidden"
              onClick={toggleMobileMenu}
            />
          )}
          <nav className="flex items-center space-x-4 max-lg:hidden">
          {NavigationLines.map((line, i) =>
              line.link && !line.groups ? (
                <CustomLink
                  key={`${line.name}-${i}-link`}
                  href={line.link}
                  className="flex items-center gap-2 text-gray-700 hover:text-black"
                  onClick={() => {
                    setActiveDropdown(undefined);
                    setOpenedDropdown({ menu: "", submenu: "" });
                  }}
                >
                  {line.name}
                </CustomLink>
              ) : (
                <div key={line.src ?? "" + i + "a"} className="group relative">
                  <button
                    className="flex items-center gap-2 text-gray-700 hover:text-black"
                    onClick={() => {
                      if (activeDropdown?.name == line.name) {
                        setActiveDropdown(undefined);
                      } else {
                        setActiveDropdown(line);
                      }
                    }}
                  >
                    {line.name}
                    <BsChevronDown
                      className={`${
                        activeDropdown?.name == line.name ? "rotate-180" : ""
                      } transition-all duration-300`}
                    />
                  </button>
                </div>
              ),
            )}
            <CustomLink
              href={"/kinto"}
              className="flex items-center gap-2 text-gray-700 hover:text-black"
            >
              KINTO
            </CustomLink>
          </nav>
          <div className="hidden min-[1140px]:flex items-end">
            <button
              onClick={() => {
                setAlertIsOpen(true);
              }}
              className=" animate-pulse-text flex w-fit ml-auto items-center text-left text-sm text-gray-700 hover:text-black"
            >
              Alerta aos consumidores
            </button>
            <RiAlertFill size={20} className=" text-red-500" />
          </div>
        </div>
      </header>

      <div
        key={"divACtiveContent"}
        className={`w-screen max-lg:hidden justify-between flex fixed transition-all duration-500 -mt-2 bg-white h-96 z-[39] shadow-lg shadow-black/40 dropdown-container ${
          activeDropdown ? "top-16" : "-top-96"
        }`}
      >
        {activeDropdown && (
          <>
            <img
              width={400}
              height={400}
              loading="lazy"
              src={activeDropdown?.src!}
              alt="Imagem-Dropdown"
              className="h-full w-auto"
            />
            <div className="h-full w-full flex flex-wrap flex-col pt-4">
              {activeDropdown.groups?.map((group, i) => (
                <div
                  key={`group-${group.name}-${i}`}
                  className="text-black h-fit leading-7 mt-5"
                >
                  <p className="mb-2 text-sm">{group.name}</p>
                  <ul className="text-neutral-400 pl-4 flex flex-col gap-2">
                    {group.itens.map((item, index) => (
                      <li key={`item-${group.name}-${item.name}-${index}`}>
                        {item.action === "open-sac-modal" ? (
                          <button
                            type="button"
                            onClick={openSacModalFromMenu}
                            className="hover:text-black flex items-center transition-all text-base text-left"
                          >
                            {item.name}
                          </button>
                        ) : item.action === "cookie-preferences" ? (
                          <CookiePreferencesButton className="hover:text-black flex items-center transition-all text-base text-left">
                            {item.name}
                          </CookiePreferencesButton>
                        ) : (
                          <CustomLink
                            href={item.link || "#"}
                            className="hover:text-black flex items-center transition-all text-base"
                            target={item.target}
                          >
                            {item.name}
                            {item.target && (
                              <BiLinkExternal
                                className="ml-2 mb-[2px]"
                                size={16}
                              />
                            )}
                          </CustomLink>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div
        key={"123"}
        className={`w-screen h-screen transition-all pt-10 lg:hidden fixed z-[39] duration-500 flex flex-col bg-white overflow-y-auto ${
          mobileMenu ? (show ? "top-12" : "top-6") : "-top-[990px]"
        }`}
      >
        {NavigationLines.map((line, i) =>
          line.link && !line.groups ? (
            <CustomLink
              key={`${line.name}-${i}-mobile-link`}
              href={line.link}
              className="w-full p-4 border-b flex justify-between text-black"
              onClick={() => {
                setMobileMenu(false);
                setOpenedDropdown({ menu: "", submenu: "" });
                document.body.classList.remove("overflow-hidden");
              }}
            >
              {line.name}
            </CustomLink>
          ) : (
          <div className={`w-full`} key={line.src + line.name + i + "line"}>
            <div
              onClick={() => {
                setOpenedDropdown({
                  ...openedDropdown,
                  menu: openedDropdown.menu == line.name ? "" : line.name,
                });
              }}
              className="w-full p-4 border-b flex justify-between transition-all duration-700 text-black"
            >
              <p>{line.name}</p>
              <BiChevronDown
                size={20}
                className={`text-black transition-all duration-300 ${
                  openedDropdown.menu == line.name ? "rotate-180" : ""
                }`}
              />
            </div>
            {openedDropdown.menu == line.name && line.groups && (
              <div className="w-full flex flex-col items-center transition-all duration-700 h-fit overflow-y-hidden">
                {line.groups.length > 1 ? (
                  line.groups.map((group, i) => (
                    <div className="w-full " key={group.name + i + "c"}>
                      <div
                        onClick={() => {
                          setOpenedDropdown({
                            ...openedDropdown,
                            submenu:
                              openedDropdown.submenu == group.name
                                ? ""
                                : group.name,
                          });
                        }}
                        className="w-10/12 p-4 border-b mx-auto text-sm flex justify-between text-black"
                      >
                        <p>{group.name}</p>
                        <BiChevronDown
                          size={20}
                          className={`text-black transition-all duration-300 ${
                            openedDropdown.submenu == group.name
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </div>
                      <div
                        className={`w-full grid bg-[#f5f5f5]  justify-start pl-8 text-black  border-b h-fit overflow-y-hidden ${
                          openedDropdown.submenu == group.name
                            ? "max-h-full"
                            : "max-h-0"
                        }`}
                      >
                        {group.itens.map((item) =>
                          item.action === "open-sac-modal" ? (
                            <button
                              type="button"
                              key={`${group.name}-${item.name}-sac`}
                              onClick={openSacModalFromMenu}
                              className={`text-sm p-5 w-full text-left ${
                                line.name != "Novos" ? "text-left" : "m-auto"
                              }`}
                            >
                              {item.name}
                            </button>
                          ) : item.action === "cookie-preferences" ? (
                            <CookiePreferencesButton
                              key={`${group.name}-${item.name}-cookies`}
                              className={`text-sm p-5 w-full text-left ${
                                line.name != "Novos" ? "text-left" : "m-auto"
                              }`}
                            >
                              {item.name}
                            </CookiePreferencesButton>
                          ) : (
                            <CustomLink
                              href={item.link || "#"}
                              target={item.target}
                              key={item.link + item.name + "desk"}
                              className={`text-sm  p-5 ${
                                line.name != "Novos" ? "text-left" : "m-auto"
                              }`}
                            >
                              <div className="flex flex-col gap-2 text-center">
                                {item.src && (
                                  <img
                                    width={400}
                                    height={400}
                                    loading="lazy"
                                    src={item.src}
                                    alt={`${item.name} image`}
                                    className="object-contain shrink-0 self-stretch mx-auto w-40 -my-10 aspect-square"
                                  />
                                )}
                                {item.name}
                              </div>
                            </CustomLink>
                          )
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="w-full grid  text-black  transition-all duration-700 h-fit overflow-y-hidden text-center">
                    {line.groups[0]?.itens.map((item) =>
                      item.action === "open-sac-modal" ? (
                        <button
                          type="button"
                          key={item.name + "mbl-sac"}
                          onClick={openSacModalFromMenu}
                          className={`text-sm p-5 border-b bg-[#f5f5f5] w-full text-left ${
                            line.name != "Novos" ? "text-left" : "m-auto"
                          }`}
                        >
                          {item.name}
                        </button>
                      ) : item.action === "cookie-preferences" ? (
                        <CookiePreferencesButton
                          key={item.name + "mbl-cookies"}
                          className={`text-sm p-5 border-b bg-[#f5f5f5] w-full text-left ${
                            line.name != "Novos" ? "text-left" : "m-auto"
                          }`}
                        >
                          {item.name}
                        </CookiePreferencesButton>
                      ) : (
                        <CustomLink
                          href={item.link || "#"}
                          target={item.target}
                          key={item.link + "mbl" + item.name}
                          className={`text-sm p-5 border-b bg-[#f5f5f5] ${
                            line.name != "Novos" ? "text-left" : "m-auto"
                          }`}
                        >
                          {item.name}
                        </CustomLink>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          ),
        )}
        <div className="w-full">
          <CustomLink
            href={"/kinto"}
            className="w-full p-4 border-b flex justify-between text-black"
          >
            KINTO
          </CustomLink>
        </div>
      </div>

      {alertIsOpen && <AlertaModal onClose={() => setAlertIsOpen(false)} />}
      {sacModalOpen && (
        <SacContactModal onClose={() => setSacModalOpen(false)} />
      )}
    </>
  );
};
